import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, ArrowLeft, Building2, FileText, ExternalLink, Eye, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { countryName } from '@/lib/countries';

interface BidRow {
  id: string;
  cover_message: string | null;
  document_url: string | null;
  status: string;
  created_at: string;
  opportunity: { id: string; title: string; type: string };
  company: { id: string; name: string; country: string };
}

export default function CompanyBids() {
  const { t } = useTranslation();
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bids, setBids] = useState<BidRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [companyType, setCompanyType] = useState<string | null>(null);
  const [selectedBid, setSelectedBid] = useState<BidRow | null>(null);

  useEffect(() => {
    if (!authLoading && (!user || role !== 'company')) {
      navigate('/auth');
      return;
    }
    if (user) fetchBids();
  }, [user, role, authLoading]);

  const fetchBids = async () => {
    if (!user) return;
    try {
      const { data: comp } = await supabase
        .from('companies')
        .select('id, company_type')
        .eq('user_id', user.id)
        .single();

      if (!comp) { setLoading(false); return; }
      setCompanyType(comp.company_type);

      if (comp.company_type === 'congolese_sme') {
        // SME sees their own bids
        const { data } = await supabase
          .from('bids')
          .select('id, cover_message, document_url, status, created_at, opportunity:opportunities(id, title, type), company:companies(id, name, country)')
          .eq('company_id', comp.id)
          .order('created_at', { ascending: false });
        setBids((data as unknown as BidRow[]) || []);
      } else {
        // Investor sees bids received on their opportunities
        const { data: opps } = await supabase
          .from('opportunities')
          .select('id')
          .eq('company_id', comp.id);

        if (opps && opps.length > 0) {
          const oppIds = opps.map(o => o.id);
          const { data } = await supabase
            .from('bids')
            .select('id, cover_message, document_url, status, created_at, opportunity:opportunities(id, title, type), company:companies(id, name, country)')
            .in('opportunity_id', oppIds)
            .order('created_at', { ascending: false });
          setBids((data as unknown as BidRow[]) || []);
        }
      }
    } catch (error) {
      console.error('Error fetching bids:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBidStatus = async (bidId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('bids')
        .update({ status: newStatus })
        .eq('id', bidId);
      if (error) throw error;
      setBids(prev => prev.map(b => b.id === bidId ? { ...b, status: newStatus } : b));
      toast.success(`Bid ${newStatus}`);
    } catch (error) {
      console.error('Error updating bid:', error);
      toast.error('Failed to update bid status');
    }
  };

  const isSme = companyType === 'congolese_sme';
  const statusColor = (s: string) => {
    switch (s) {
      case 'accepted': return 'default';
      case 'rejected': return 'destructive';
      case 'reviewed': return 'secondary';
      default: return 'outline';
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8">
        <Button variant="ghost" className="mb-6 gap-2" onClick={() => navigate('/dashboard/company')}>
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </Button>

        <h1 className="mb-6 text-3xl font-bold">
          {isSme ? t('bids.myBids', 'My Bids') : t('bids.receivedBids', 'Received Bids')}
        </h1>

        {bids.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-12">
              <FileText className="h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground">
                {isSme
                  ? t('bids.noBidsYet', "You haven't submitted any bids yet.")
                  : t('bids.noReceivedBids', 'No bids received yet.')}
              </p>
              {isSme && (
                <Button asChild>
                  <Link to="/opportunities?type=tender,jv">{t('bids.browseTenders', 'Browse Tenders & JVs')}</Link>
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bids.map((bid) => (
              <Card key={bid.id}>
                <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <Link to={`/opportunities/${bid.opportunity?.id}`} className="font-semibold hover:underline">
                      {bid.opportunity?.title}
                    </Link>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Building2 className="h-3.5 w-3.5" />
                      {isSme ? t('bids.submittedOn', 'Submitted') : bid.company?.name}
                      <span>·</span>
                      {format(new Date(bid.created_at), 'MMM d, yyyy')}
                    </div>
                    {bid.cover_message && (
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{bid.cover_message}</p>
                    )}
                    {bid.document_url && (
                      <a
                        href={bid.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View Proposal Document
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={statusColor(bid.status) as any} className="capitalize">
                      {bid.status}
                    </Badge>
                    {isSme && (
                      <Button size="sm" variant="outline" asChild>
                        <Link to={`/opportunities/${bid.opportunity?.id}`}>
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          {t('bids.viewTender', 'View Tender')}
                        </Link>
                      </Button>
                    )}
                    {!isSme && (
                      <Button size="sm" variant="ghost" onClick={() => setSelectedBid(bid)}>
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        View
                      </Button>
                    )}
                    {!isSme && bid.status === 'pending' && (
                      <Select onValueChange={(v) => updateBidStatus(bid.id, v)}>
                        <SelectTrigger className="w-[130px]">
                          <SelectValue placeholder="Update..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="reviewed">Reviewed</SelectItem>
                          <SelectItem value="accepted">Accept</SelectItem>
                          <SelectItem value="rejected">Reject</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {/* Bid Detail Dialog (Investor view) */}
        <Dialog open={!!selectedBid} onOpenChange={(open) => !open && setSelectedBid(null)}>
          <DialogContent className="max-w-lg">
            {selectedBid && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <Building2 className="h-5 w-5" />
                    {selectedBid.company?.name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={statusColor(selectedBid.status) as any} className="capitalize">
                      {selectedBid.status}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Submitted {format(new Date(selectedBid.created_at), 'MMM d, yyyy')}
                    </span>
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Opportunity</p>
                    <Link to={`/opportunities/${selectedBid.opportunity?.id}`} className="font-medium text-primary hover:underline">
                      {selectedBid.opportunity?.title}
                    </Link>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    {countryName(selectedBid.company?.country)}
                  </div>

                  {selectedBid.cover_message && (
                    <div>
                      <p className="text-sm font-medium mb-1">Proposal Message</p>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap rounded-lg bg-muted p-3">
                        {selectedBid.cover_message}
                      </p>
                    </div>
                  )}

                  {selectedBid.document_url && (
                    <div>
                      <p className="text-sm font-medium mb-2">Proposal Document</p>
                      <a
                        href={selectedBid.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border p-3 text-sm text-primary hover:bg-muted transition-colors"
                      >
                        <FileText className="h-4 w-4" />
                        Download Proposal
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  )}

                  {!selectedBid.cover_message && !selectedBid.document_url && (
                    <p className="text-sm text-muted-foreground italic">No proposal details were provided.</p>
                  )}

                  {selectedBid.status === 'pending' && (
                    <div className="flex gap-2 pt-2 border-t">
                      <Button size="sm" variant="outline" onClick={() => { updateBidStatus(selectedBid.id, 'reviewed'); setSelectedBid(s => s ? { ...s, status: 'reviewed' } : null); }}>
                        Mark Reviewed
                      </Button>
                      <Button size="sm" onClick={() => { updateBidStatus(selectedBid.id, 'accepted'); setSelectedBid(s => s ? { ...s, status: 'accepted' } : null); }}>
                        Accept
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => { updateBidStatus(selectedBid.id, 'rejected'); setSelectedBid(s => s ? { ...s, status: 'rejected' } : null); }}>
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
